<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import type { ProductCategoryProps } from "./ProductCategoryProps.js";
	import ProductCard from "../components/Product/ProductCard.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ProductCategoryFilters from "../components/ProductCategoryFilters.svelte";
	import Branches from "../components/Branches.svelte";
	import WeddingsBanner from "../components/Wedding/WeddingsBanner.svelte";
	import Content from "../components/Content.svelte";
	import { onMount, setContext } from "svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import { PriorityLabel } from "../../../core/schema/PriorityLabel.js";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import { getClients } from "../appsync/getClients.js";
	import type { WeddingForm } from "../../../core/schema/WeddingForm.js";
	import getWeddingFormQuery from "../appsync/queries/getWeddingForm.graphql";
	import Preloader from "../components/Preloader.svelte";
	import { inflectFillingsLeft } from "../../../core/schema/inflectFillingsLeft.js";
	import { canBuyFillings } from "../../../core/schema/utils/canBuyFillings.js";
	import { getCartStore } from "../contexts/cartStoreContextKey.js";
	import { isCartEmpty } from "../stores/cart/isCartEmpty";
	import CartSummary from "../components/Cart/CartSummary.svelte";

	type $$Props = ProductCategoryProps;

	const cartStore = getCartStore();
	const { products, category, maxProductsPerCategory, requiredCategories } = $$restProps as $$Props;
	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE, STORAGE_URL } = globalSettings;

	const [thumbnailProduct] = products.toSorted((first, second) => {
		if (first.priorityLabel === PriorityLabel.Favorite) {
			return first.priorityLabel === second.priorityLabel ? 0 : -1;
		}
		return 0;
	});
	if (thumbnailProduct) {
		setContext(thumbnailImageContextKey, `${STORAGE_URL}/${thumbnailProduct.images[0]?.path}`);
	}

	const filters = Array.from(new Set(products.flatMap(({ filtrationLabels }) => filtrationLabels[TENANT_LOCALE])));
	let selectedFilters: string[] = [];

	$: filteredProducts = products.filter((product) => {
		if (selectedFilters.length === 0) {
			return true;
		}
		return selectedFilters.some((filter) => product.filtrationLabels[TENANT_LOCALE].includes(filter));
	});

	const getWeddingForm = createPromiseStore(async (id: WeddingForm["id"]) => {
		const { graphQL } = await getClients(globalSettings);
		const weddingForm = await graphQL.query<WeddingForm, { id: WeddingForm["id"] }>({
			query: getWeddingFormQuery,
			variables: {
				id,
			},
		});

		if (!canBuyFillings(weddingForm)) {
			return false;
		}

		localStorage.setItem("weddingFormId", weddingForm.id);

		return true;
	});

	onMount(async () => {
		if (category.id !== requiredCategories.fillings.id) {
			return;
		}

		const urlParams = new URLSearchParams(window.location.search);
		const weddingFormId = urlParams.get("id");

		if (!weddingFormId) {
			window.location.href = "/";
		} else {
			const response = await getWeddingForm.call(weddingFormId);
			if (!response) {
				window.location.href = "/";
			}
		}
	});

	$: productsOfCategoryInCart = $cartStore.value.order.products.reduce((acc, product) => {
		const productDetail = $cartStore.value.order.products.find(({ id }) => product.id === id);
		if (!productDetail?.product.categoryIds.includes(category.id)) {
			return acc;
		}
		return acc + product.quantity;
	}, 0);
	$: hasItemsInCart = !isCartEmpty($cartStore.value);
</script>

<Layout hasPanel={hasItemsInCart && category.id !== requiredCategories.fillings.id}>
	<div class="container">
		{#if category.id === requiredCategories.fillings.id && ($getWeddingForm.status !== "success" || !$getWeddingForm.result)}
			<Preloader />
		{:else}
			<section>
				<h1
					class="mb-8 mt-5 p-0 text-center text-[2rem] font-semibold leading-10 lg:mb-[1.625rem] lg:mt-0 lg:text-xl lg:leading-[3.25rem]"
				>
					{nbspify(category.name[TENANT_LOCALE])}
				</h1>
				<div class="mx-auto my-4 max-w-[700px] text-base">
					<Content content={category.description[TENANT_LOCALE]} />
				</div>
				{#if filters.length > 0}
					<ProductCategoryFilters {filters} bind:selectedFilters />
				{/if}

				<div class="product-card-wrapper">
					{#each filteredProducts as product, index (product.id)}
						<ProductCard {product} {index} {maxProductsPerCategory} />
					{/each}
				</div>

				{#if category.id === requiredCategories.fillings.id && maxProductsPerCategory[category.id] !== undefined}
					{@const maxFillings = Number(maxProductsPerCategory[category.id])}
					{@const fillings = productsOfCategoryInCart}
					<div
						class="pointer-events-none fixed bottom-0 left-0 z-40 mb-9 w-full text-center sm:left-auto sm:right-[50%] sm:translate-x-[50%] md:mb-6 md:w-fit"
					>
						<div
							class="pointer-events-auto mx-3 hidden h-[calc(100%-2.25rem)] flex-col items-center justify-center gap-2 rounded-full bg-white px-16 py-4 text-xs font-semibold shadow-[0_0_15px_rgba(0,0,0,0.2)] sm:flex sm:flex-row sm:gap-12 sm:text-sm md:text-base lg:gap-16 xl:gap-20"
						>
							{#if maxFillings - fillings > 0}
								<p>
									Můžeš si vybrat až {maxFillings}
									{inflectFillingsLeft(maxFillings)}. Máš vybrané {fillings} z {maxFillings}
								</p>
							{:else}
								<p>Do košíku již nelze přidat žádné další náplně</p>
							{/if}
						</div>
						<div
							class="pointer-events-auto mx-3 flex h-[calc(100%-2.25rem)] flex-col items-center justify-center gap-2 rounded-full bg-white px-16 py-4 text-xs font-semibold shadow-[0_0_15px_rgba(0,0,0,0.2)] sm:hidden sm:flex-row sm:gap-12 sm:text-sm md:text-base lg:gap-16 xl:gap-20"
						>
							{#if maxFillings - fillings > 0}
								<p>
									Máš vybrané {fillings} z {maxFillings}
									{inflectFillingsLeft(maxFillings)}
								</p>
							{:else}
								<p>Do košíku již nelze přidat žádné další náplně</p>
							{/if}
						</div>
					</div>
				{/if}
			</section>
		{/if}

		{#if category.id !== requiredCategories.fillings.id}
			<CartSummary linkToCart buttonText="Pokračovat v objednávce" />
		{/if}

		<section>
			<WeddingsBanner />
		</section>

		<section>
			<Branches />
		</section>
	</div>
</Layout>
